<template>
  <section id="business" class="business-section">
    <div class="container">
      <div class="section-title">
        <div class="en-label">{{ $t('business.eyebrow') }}</div>
        <h2>{{ $t('business.title') }}</h2>
        <p>{{ $t('business.desc') }}</p>
      </div>

      <div class="business-tabs">
        <div class="tab-nav">
          <div
            v-for="(tab, idx) in tabs"
            :key="tab.id"
            class="tab-item"
            :class="{ active: activeTab === idx }"
            @click="activeTab = idx">
            <div class="tab-icon" :style="{ background: tab.color }">
              <i :class="tab.icon"></i>
            </div>
            <div class="tab-label">
              <span class="tab-en">{{ tab.en }}</span>
              <span class="tab-zh">{{ tab.zh }}</span>
            </div>
          </div>
        </div>

        <transition name="fade-slide" mode="out-in">
          <div class="tab-content" :key="activeTab">
            <div class="content-left">
              <div class="content-badge" :style="{ color: tabs[activeTab].accent }">
                {{ tabs[activeTab].en }}
              </div>
              <h3>{{ tabs[activeTab].title }}</h3>
              <p>{{ tabs[activeTab].desc }}</p>

              <ul class="feature-list">
                <li v-for="item in tabs[activeTab].features" :key="item.id">
                  <span class="feature-dot" :style="{ background: tabs[activeTab].accent }"></span>
                  <span><strong>{{ item.title }}</strong>{{ item.text }}</span>
                </li>
              </ul>

              <div class="metrics">
                <div class="metric-item" v-for="metric in tabs[activeTab].metrics" :key="metric.id">
                  <strong :style="{ color: tabs[activeTab].accent }">{{ metric.value }}</strong>
                  <span>{{ metric.label }}</span>
                </div>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </div>
  </section>
</template>

<script>
const tabMeta = {
  'ai-audit': {
    icon: 'el-icon-finished',
    color: 'linear-gradient(135deg,#00c9a7,#00b894)',
    accent: '#00b894'
  },
  pbrtqc: {
    icon: 'el-icon-first-aid-kit',
    color: 'linear-gradient(135deg,#0a73b7,#1a8fc0)',
    accent: '#0a73b7'
  },
  'prediction-twin': {
    icon: 'el-icon-cpu',
    color: 'linear-gradient(135deg,#6c5ce7,#8e7cf4)',
    accent: '#6c5ce7'
  }
}

export default {
  name: 'BusinessSection',
  data() {
    return {
      activeTab: 0
    }
  },
  computed: {
    tabs() {
      return this.$tm('business.tabs').map(tab => ({
        ...tab,
        ...tabMeta[tab.id]
      }))
    }
  }
}
</script>

<style lang="less" scoped>
.business-section {
  padding: 92px 0 120px;
  background: var(--bg-light);
}

.business-tabs {
  background: #fff;
  border-radius: 20px;
  box-shadow: 0 20px 54px rgba(21, 45, 71, 0.09);
  overflow: hidden;
}

.tab-nav {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  border-bottom: 1px solid #e8edf3;
  background: #fbfcff;
}

.tab-item {
  display: flex;
  align-items: center;
  gap: 14px;
  min-height: 92px;
  padding: 22px 28px;
  cursor: pointer;
  transition: all 0.25s;
  border-right: 1px solid #e8edf3;
  border-bottom: 3px solid transparent;

  &:last-child {
    border-right: none;
  }

  &.active {
    background: #fff;
    border-bottom-color: var(--primary);

    .tab-zh { color: var(--primary); }
  }

  &:hover:not(.active) {
    background: #f5f8fb;
  }
}

.tab-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 20px;
  flex-shrink: 0;
}

.tab-label {
  display: flex;
  flex-direction: column;
  line-height: 1.35;
}

.tab-en {
  font-size: 11px;
  letter-spacing: 1.4px;
  color: var(--text-light);
  font-weight: 700;
}

.tab-zh {
  font-size: 16px;
  font-weight: 800;
  color: var(--text-dark);
  transition: color 0.2s;
}

.tab-content {
  min-height: 532px;
}

.content-left {
  padding: 52px 48px;
  max-width: 760px;
}

.content-badge {
  font-size: 12px;
  letter-spacing: 5px;
  font-weight: 800;
  margin-bottom: 16px;
}

h3 {
  font-size: 28px;
  font-weight: 800;
  color: var(--text-dark);
  margin-bottom: 18px;
  line-height: 1.35;
}

.content-left > p {
  font-size: 16px;
  color: var(--text-mid);
  line-height: 1.9;
  margin-bottom: 30px;
}

.feature-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin: 0;
  padding: 0 0 30px;
  border-bottom: 1px solid #edf0f4;
  list-style: none;
}

.feature-list li {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  font-size: 14px;
  line-height: 1.65;
  color: var(--text-mid);
}

.feature-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-top: 8px;
  flex-shrink: 0;
}

.feature-list strong {
  color: var(--text-dark);
  font-weight: 800;
}

.metrics {
  display: flex;
  gap: 46px;
  padding-top: 28px;
  flex-wrap: wrap;
}

.metric-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.metric-item strong {
  font-size: 26px;
  line-height: 1;
  font-weight: 900;
}

.metric-item span {
  font-size: 12px;
  color: var(--text-light);
}

.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s ease;
}

.fade-slide-enter {
  opacity: 0;
  transform: translateY(12px);
}

.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

@media (max-width: 900px) {
  .tab-nav {
    grid-template-columns: 1fr;
  }

  .tab-item {
    min-height: 76px;
    border-right: none;
  }

  .tab-content {
    min-height: auto;
  }

  .content-left {
    padding: 36px 24px;
    max-width: none;
  }
}
</style>
